'use client';

import { alphaVantageService, AlphaVantageQuote } from './alphaVantageService';
import { yahooFinanceService, YahooFinanceQuote } from './yahooFinanceService';
import { finnhubService, FinnhubStockQuote } from './finnhubService';
import { realtimeAPI } from './financialDataService';

export type APIProvider = 'alphavantage' | 'yahoofinance' | 'finnhub' | 'default';

export interface UnifiedQuote {
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
  high: number;
  low: number;
  open: number;
  previousClose: number;
  volume?: number;
  provider: APIProvider;
}

/**
 * Unified Financial API Adapter
 * Provides a single interface to interact with multiple financial data providers
 */
export class FinancialAPIAdapter {
  private provider: APIProvider;

  constructor(provider: APIProvider = 'alphavantage') {
    this.provider = provider;
  }

  /**
   * Set the active API provider
   */
  setProvider(provider: APIProvider) {
    this.provider = provider;
  }

  /**
   * Get current provider
   */
  getProvider(): APIProvider {
    return this.provider;
  }

  /**
   * Fetch quote from the active provider
   */
  async getQuote(symbol: string): Promise<UnifiedQuote | null> {
    switch (this.provider) {
      case 'finnhub':
        return this.getFinnhubQuote(symbol);
      case 'alphavantage':
        return this.getAlphaVantageQuote(symbol);
      case 'yahoofinance':
        return this.getYahooFinanceQuote(symbol);
      case 'default':
      default:
        return this.getDefaultQuote(symbol);
    }
  }

  /**
   * Fetch quotes for multiple symbols
   */
  async getMultipleQuotes(symbols: string[]): Promise<Map<string, UnifiedQuote>> {
    const results = new Map<string, UnifiedQuote>();

    switch (this.provider) {
      case 'finnhub': {
        const quotes = await finnhubService.getMultipleQuotes(symbols);
        quotes.forEach((quote, symbol) => {
          results.set(symbol, this.normalizeFinnhubQuote(quote));
        });
        break;
      }
      case 'alphavantage': {
        const quotes = await alphaVantageService.getMultipleQuotes(symbols);
        quotes.forEach((quote, symbol) => {
          results.set(symbol, this.normalizeAlphaVantageQuote(quote));
        });
        break;
      }
      case 'yahoofinance': {
        const quotes = await yahooFinanceService.getMultipleQuotes(symbols);
        quotes.forEach((quote, symbol) => {
          results.set(symbol, this.normalizeYahooFinanceQuote(quote));
        });
        break;
      }
      case 'default':
      default: {
        const quotes = await realtimeAPI.fetchMultipleSymbols(symbols);
        quotes.forEach((data, symbol) => {
          const normalized = this.normalizeDefaultQuote(symbol, data);
          if (normalized) {
            results.set(symbol, normalized);
          }
        });
        break;
      }
    }

    return results;
  }

  /**
   * Search for symbols across providers
   */
  async searchSymbols(query: string) {
    switch (this.provider) {
      case 'alphavantage':
        return alphaVantageService.searchSymbols(query);
      case 'yahoofinance':
        return yahooFinanceService.searchSymbols(query);
      default:
        console.warn('Symbol search not available for default provider');
        return null;
    }
  }

  /**
   * Get market news
   */
  async getMarketNews(category: string = 'general') {
    console.warn('Market news not available with Yahoo Finance provider');
    return [];
  }

  /**
   * Get company profile
   */
  async getCompanyProfile(symbol: string) {
    console.warn('Company profile not available with Yahoo Finance provider');
    return null;
  }

  /**
   * Get forex rates
   */
  async getForexRate(fromCurrency: string, toCurrency: string) {
    switch (this.provider) {
      case 'alphavantage':
        return alphaVantageService.getForexRate(fromCurrency, toCurrency);
      case 'yahoofinance':
        return yahooFinanceService.getQuote(`${fromCurrency}${toCurrency}=X`);
      default:
        return realtimeAPI.fetchForexRates();
    }
  }

  // Private helper methods to normalize data from different providers

  private async getAlphaVantageQuote(symbol: string): Promise<UnifiedQuote | null> {
    const quote = await alphaVantageService.getQuote(symbol);
    return quote ? this.normalizeAlphaVantageQuote(quote) : null;
  }

  private async getYahooFinanceQuote(symbol: string): Promise<UnifiedQuote | null> {
    const quote = await yahooFinanceService.getQuote(symbol);
    return quote ? this.normalizeYahooFinanceQuote(quote) : null;
  }

  private async getDefaultQuote(symbol: string): Promise<UnifiedQuote | null> {
    const data = await realtimeAPI.fetchLiveMarketData(symbol);
    return data ? this.normalizeDefaultQuote(symbol, data) : null;
  }

  private normalizeAlphaVantageQuote(quote: AlphaVantageQuote): UnifiedQuote {
    return {
      symbol: quote.symbol,
      price: quote.price,
      change: quote.change,
      changePercent: quote.changePercent,
      high: quote.high,
      low: quote.low,
      open: quote.open,
      previousClose: quote.previousClose,
      volume: quote.volume,
      provider: 'alphavantage',
    };
  }

  private normalizeYahooFinanceQuote(quote: YahooFinanceQuote): UnifiedQuote {
    return {
      symbol: quote.symbol,
      price: quote.price,
      change: quote.change,
      changePercent: quote.changePercent,
      high: quote.high,
      low: quote.low,
      open: quote.open,
      previousClose: quote.previousClose,
      volume: quote.volume,
      provider: 'yahoofinance',
    };
  }

  private normalizeDefaultQuote(symbol: string, data: any): UnifiedQuote | null {
    if (!data) return null;

    return {
      symbol,
      price: data.price || data.c || 0,
      change: data.change || data.d || 0,
      changePercent: data.changePercent || data.dp || 0,
      high: data.high || data.h || 0,
      low: data.low || data.l || 0,
      open: data.open || data.o || 0,
      previousClose: data.previousClose || data.pc || 0,
      volume: data.volume,
      provider: 'default',
    };
  }

  /**
   * Normalize Finnhub quote to unified format
   */
  private normalizeFinnhubQuote(quote: FinnhubStockQuote): UnifiedQuote {
    return {
      symbol: quote.symbol,
      price: quote.price,
      change: quote.change,
      changePercent: quote.changePercent,
      high: quote.high,
      low: quote.low,
      open: quote.open,
      previousClose: quote.previousClose,
      volume: undefined,
      provider: 'finnhub',
    };
  }

  /**
   * Fetch quote from Finnhub
   */
  private async getFinnhubQuote(symbol: string): Promise<UnifiedQuote | null> {
    const quote = await finnhubService.getQuote(symbol);
    if (!quote) return null;
    return this.normalizeFinnhubQuote(quote);
  }
}

// Export singleton instance
export const financialAPI = new FinancialAPIAdapter('alphavantage');

// Export convenience methods
export const setAPIProvider = (provider: APIProvider) => {
  financialAPI.setProvider(provider);
};

export const getAPIProvider = () => {
  return financialAPI.getProvider();
};